// Utility to schedule a callback after the current render/paint cycle.
// Replaces ad-hoc setTimeout(fn, 0) layout hacks with a predictable queue.
// Uses requestAnimationFrame when available to align with browser paint.
// Falls back to setTimeout for environments without RAF.
// Multiple callbacks queued within the same tick are batched and flushed together.

const callbackQueue: Array<() => void> = [];
let scheduled = false;

function flushQueue() {
  scheduled = false;
  // Copy then clear to protect against re-entrancy pushing new items mid-flush.
  const toRun = callbackQueue.slice();
  callbackQueue.length = 0;
  for (const cb of toRun) {
    try {
      cb();
    } catch (err) {
      // Swallow errors so remaining callbacks still run; surface via console for now.
      // A future enhancement can integrate AppInsightsService here once core/error module is in place.
      console.error('afterRender callback error', err);
    }
  }
}

function scheduleFlush() {
  if (scheduled) return;
  scheduled = true;
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      // Use a microtask to ensure any RAF layout side-effects settle first.
      Promise.resolve().then(flushQueue);
    });
  } else {
    setTimeout(flushQueue, 0);
  }
}

export function afterRender(cb: () => void): void {
  callbackQueue.push(cb);
  scheduleFlush();
}

// Optional convenience to await completion of queued callbacks.
export function afterRenderAsync(cb?: () => void): Promise<void> {
  return new Promise((resolve) => {
    afterRender(() => {
      try {
        cb?.();
      } finally {
        resolve();
      }
    });
  });
}

// Debug helper to inspect queue depth (could be removed in production once stable).
export function getAfterRenderQueueSize(): number {
  return callbackQueue.length;
}
